import * as React from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  getMyPartnerDashboard,
  submitNamedReferral,
  NAMED_REFERRAL_WINDOW_DAYS,
  REDITUS_MARKETPLACE_HREF,
} from "@/lib/partners.functions";
import { MISSING_RESOURCE_COPY } from "@/lib/partner-resources";

export const Route = createFileRoute("/_authed/partners/dashboard")({
  head: () => ({
    meta: [
      { title: "Partner dashboard: CapitalDart" },
      { name: "robots", content: "noindex,nofollow" },
    ],
  }),
  component: PartnerDashboardPage,
});

function money(cents: number): string {
  return `$${(cents / 100).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

const STATUS_LABEL: Record<string, string> = {
  submitted: "Submitted",
  signed_up: "Signed up",
  purchased: "Purchased",
  expired: "Expired",
  rejected: "Not credited",
};

const REFERRAL_ERRORS: Record<string, string> = {
  not_a_partner: "We could not find a partner record for this account.",
  not_approved: "Your application is still under review.",
  invalid_email: "That email address does not look right.",
  self_referral: "You cannot name your own address.",
  already_claimed: "Another partner named this contact first.",
  duplicate: "You have already named this contact.",
};

function PartnerDashboardPage() {
  const qc = useQueryClient();
  const load = useServerFn(getMyPartnerDashboard);
  const submit = useServerFn(submitNamedReferral);
  const dash = useQuery({ queryKey: ["partner-dashboard"], queryFn: () => load() });

  const [companyName, setCompanyName] = React.useState("");
  const [contactName, setContactName] = React.useState("");
  const [contactEmail, setContactEmail] = React.useState("");
  const [notes, setNotes] = React.useState("");
  const [copied, setCopied] = React.useState(false);

  const create = useMutation({
    mutationFn: () =>
      submit({
        data: {
          companyName: companyName.trim(),
          contactName: contactName.trim() || null,
          contactEmail: contactEmail.trim(),
          notes: notes.trim() || null,
        },
      }),
    onSuccess: (res) => {
      if (!res.ok) return;
      setCompanyName("");
      setContactName("");
      setContactEmail("");
      setNotes("");
      qc.invalidateQueries({ queryKey: ["partner-dashboard"] });
    },
  });

  if (dash.isLoading) {
    return <main className="mx-auto max-w-[960px] px-4 py-10 text-brand-muted">Loading…</main>;
  }

  const partner = dash.data?.partner ?? null;

  if (!partner) {
    return (
      <main className="mx-auto max-w-[720px] px-4 py-12">
        <h1 className="font-serif text-[30px] text-brand-ink">Partner dashboard.</h1>
        <p className="mt-2 text-[15px] leading-relaxed text-brand-muted">
          This account is not a partner yet.{" "}
          <Link to="/partners/apply" className="underline">
            Apply to partner
          </Link>{" "}
          and we will reply by email.
        </p>
      </main>
    );
  }

  if (partner.status !== "approved") {
    return (
      <main className="mx-auto max-w-[720px] px-4 py-12">
        <h1 className="font-serif text-[30px] text-brand-ink">Partner dashboard.</h1>
        <p className="mt-2 text-[15px] leading-relaxed text-brand-muted">
          Your application is {partner.status}. We review every application by
          hand and will reply by email. Your link, the named referral form and
          the resource hub appear here once you are approved.
        </p>
      </main>
    );
  }

  const earnings = dash.data!.earnings;
  const referrals = dash.data!.referrals;
  const link = dash.data!.referralLink;

  return (
    <main className="mx-auto max-w-[960px] px-4 pb-20 pt-6">
      <h1 className="font-serif text-[32px] font-normal leading-tight tracking-tight text-brand-ink">
        Partner dashboard.
      </h1>
      <p className="mt-1 max-w-[70ch] text-[14.5px] leading-relaxed text-brand-muted">
        You earn {partner.commission_pct}% of every CapitalDart purchase you
        send. Compensation is a share of software revenue only. It is never
        tied to whether a raise closes or how large it is.
      </p>

      <section className="mt-6 grid gap-3 sm:grid-cols-4">
        <Stat label="Earned to date" value={money(earnings.earnedCents)} />
        <Stat label="Next payment" value={earnings.nextPaymentDate} />
        <Stat label="Named referrals open" value={String(earnings.pendingReferrals)} />
        <Stat label="Purchased" value={String(earnings.purchasedReferrals)} />
      </section>

      <section className="mt-8 rounded-2xl border border-brand-line bg-brand-paper p-5">
        <h2 className="font-serif text-[20px] text-brand-ink">Your link</h2>
        {link ? (
          <div className="mt-2 flex flex-wrap items-center gap-3">
            <code className="rounded bg-white px-3 py-2 font-mono text-[13px] text-brand-ink">
              {link}
            </code>
            <Button
              variant="outline"
              onClick={async () => {
                try {
                  await navigator.clipboard.writeText(link);
                  setCopied(true);
                  setTimeout(() => setCopied(false), 1500);
                } catch {
                  setCopied(false);
                }
              }}
            >
              {copied ? "Copied" : "Copy link"}
            </Button>
          </div>
        ) : (
          <p className="mt-2 text-[14px] text-brand-muted">
            Your link is being set up. Named referrals work in the meantime.
          </p>
        )}
        <p className="mt-2 text-[12.5px] text-brand-muted">
          Payments are made through our partner platform.{" "}
          <a
            href={REDITUS_MARKETPLACE_HREF}
            target="_blank"
            rel="noopener noreferrer"
            className="underline"
          >
            Open the payment platform
          </a>
          .
        </p>
      </section>

      <section className="mt-6 rounded-2xl border border-brand-line bg-brand-paper p-5">
        <h2 className="font-serif text-[20px] text-brand-ink">Name a referral</h2>
        <p className="mt-1 max-w-[70ch] text-[13.5px] leading-relaxed text-brand-muted">
          Most referrals happen in conversation, not through a click. Name the
          company and the person you spoke to. If they sign up within{" "}
          {NAMED_REFERRAL_WINDOW_DAYS} days you are credited, whatever route
          the signup came by.
        </p>
        <form
          className="mt-4 space-y-3"
          onSubmit={(e) => {
            e.preventDefault();
            create.mutate();
          }}
        >
          <div>
            <Label htmlFor="nr-company">Company</Label>
            <Input
              id="nr-company"
              required
              maxLength={200}
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="nr-contact">Contact name (optional)</Label>
            <Input
              id="nr-contact"
              maxLength={200}
              value={contactName}
              onChange={(e) => setContactName(e.target.value)}
            />
          </div>
          <div>
            <Label htmlFor="nr-email">Contact email</Label>
            <Input
              id="nr-email"
              type="email"
              required
              maxLength={320}
              value={contactEmail}
              onChange={(e) => setContactEmail(e.target.value)}
            />
            <p className="mt-1 text-[11.5px] text-brand-muted">
              Use their work address. We match on that address, and on their
              company domain when a colleague signs up instead.
            </p>
          </div>
          <div>
            <Label htmlFor="nr-notes">Notes (optional)</Label>
            <Textarea
              id="nr-notes"
              rows={3}
              maxLength={2000}
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
            />
          </div>
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? "Submitting…" : "Submit referral"}
          </Button>
          {create.data && !create.data.ok && (
            <p className="text-[12.5px] text-destructive">
              {REFERRAL_ERRORS[create.data.reason] ?? "We could not save that."}
            </p>
          )}
          {create.data?.ok && (
            <p className="text-[12.5px] text-[oklch(0.45_0.087_155.7)]">Referral submitted.</p>
          )}
        </form>
      </section>

      <section className="mt-6">
        <h2 className="mb-3 font-serif text-[20px] text-brand-ink">Your referrals</h2>
        {referrals.length === 0 ? (
          <p className="text-[14px] text-brand-muted">Nothing named yet.</p>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-brand-line bg-brand-paper">
            <table className="w-full text-[14px]">
              <thead className="bg-brand-paper-2 text-left text-[11.5px] uppercase tracking-wider text-brand-muted">
                <tr>
                  <th className="px-3 py-2">Company</th>
                  <th className="px-3 py-2">Contact</th>
                  <th className="px-3 py-2">Submitted</th>
                  <th className="px-3 py-2">Window ends</th>
                  <th className="px-3 py-2">Status</th>
                  <th className="px-3 py-2">Earned</th>
                </tr>
              </thead>
              <tbody>
                {referrals.map((r) => (
                  <tr key={r.id} className="border-t border-brand-line">
                    <td className="px-3 py-2">{r.company_name}</td>
                    <td className="px-3 py-2 text-brand-muted">
                      {r.contact_name ? `${r.contact_name}, ` : ""}
                      {r.contact_email}
                    </td>
                    <td className="px-3 py-2 text-brand-muted">
                      {new Date(r.submitted_at).toLocaleDateString()}
                    </td>
                    <td className="px-3 py-2 text-brand-muted">
                      {new Date(r.expires_at).toLocaleDateString()}
                    </td>
                    <td className="px-3 py-2">{STATUS_LABEL[r.status] ?? r.status}</td>
                    <td className="px-3 py-2 tabular-nums">{money(r.commission_cents)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <section className="mt-8 rounded-2xl border border-brand-line bg-brand-paper p-5">
        <h2 className="font-serif text-[20px] text-brand-ink">Resource hub</h2>
        <p className="mt-1 text-[13.5px] leading-relaxed text-brand-muted">
          A client introduction email, a one-page explainer, website and
          newsletter copy, five LinkedIn posts, objection handling and the
          compliance one-pager.{" "}
          {MISSING_RESOURCE_COPY.length > 0
            ? `${MISSING_RESOURCE_COPY.length} pieces are still placeholder text.`
            : "Everything is final copy."}
        </p>
        <Link
          to="/partners/resources"
          className="mt-3 inline-block text-[14px] font-semibold text-brand-ink underline"
        >
          Open the resource hub
        </Link>
      </section>
    </main>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-brand-line bg-brand-paper p-4">
      <div className="text-[11.5px] uppercase tracking-wider text-brand-muted">{label}</div>
      <div className="mt-1 font-serif text-[22px] text-brand-ink tabular-nums">{value}</div>
    </div>
  );
}
